import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// ========== 全局共享金价（单例） ==========
interface PriceState {
  label: string;
  price: number;       // ask 销售价
  bidPrice: number;    // bid 回购价
  basePrice: number;
  prevPrice: number;
  changePct: number;
}

let _state: Record<string, PriceState> = {};
let _history: Record<string, number[]> = {};
let _initialized = false;
let _intervalId: ReturnType<typeof setInterval> | null = null;
let _listeners = new Set<() => void>();
let _initPromise: Promise<void> | null = null;

let _isLive = false;
let _lastRealTick = 0;
let _failCount = 0;
const MAX_FAIL_COUNT = 3;
// 超过该时长没收到任何新报价，才判定为「行情未连接」。
// 休市时行情源本就不推送新报价，阈值太短会把「休市」误报成「断连」，
// 也会被 5 秒一次的兜底轮询间隔误伤（轮询本身 2~3 秒往返）。
const STALE_MS = 60000;
let _simTimer: ReturnType<typeof setInterval> | null = null;
let _waveTimers: Record<string, ReturnType<typeof setInterval>> = {};

// HTTP 轮询
let _httpPollTimer: ReturnType<typeof setInterval> | null = null;
let _httpPollActive = false;
let _httpPollUrl = '';

let _ws: WebSocket | null = null;
let _wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _wsReconnectCount = 0;
const WS_URL = 'wss://500mapi2h.gjshs.top/wss';

function parsePrice(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^-.\d]/g, '');
    return parseFloat(cleaned) || 0;
  }
  return 0;
}

const DEFAULT_PRICES: Record<string, number> = {
  '黄金': 954.0,
  '白银': 14.8,
  '铂金': 374.0,
  '钯金': 281.0,
  '铑金': 4500.0,
  '美铑金': 4600.0,
  'Au9999': 956.0,
  'AuT+D': 956.0,
  'AgT+D': 7800.0,
  'Pt9995': 220.0,
  '港金': 28000.0,
  '公斤条': 954.0,
  '美元指数': 103.2,
  '美元人民币': 7.25,
  '伦敦金': 2800.0,
  '伦敦银': 32.8,
  '伦敦铂': 990.0,
  '伦敦钯': 1030.0,
  '美黄金': 2800.0,
  '美白银': 32.8,
  '美铂金': 990.0,
  '美钯金': 1030.0,
  '金条价': 954.0,
  '黄金9999': 956.0,
  'RKGRMB': 954.0,
  'SKGRMB': 954.0,
  'RKGSIV': 14.8,
  'AU9999': 956.0,
  'AG9999': 7800.0,
};

const PRICE_ALIASES: Record<string, string[]> = {
  '黄金': ['金条价', 'RKGRMB', 'SKGRMB'],
  'Au9999': ['黄金9999', 'AU9999'],
  'AgT+D': ['AG9999'],
};

const WS_TO_LABEL: Record<string, string> = {
  xau: '黄金',
  xag: '白银',
  xpt: '铂金',
  xpd: '钯金',
  laojin: '铑金',
  meilaojin: '美铑金',
  SGAu9999: 'Au9999',
  'SGAuT+D': 'AuT+D',
  'SGAgT+D': 'AgT+D',
  SGPt9995: 'Pt9995',
  PMGT: '港金',
  gjt: '公斤条',
  myzs: '美元指数',
  'USD.CNH': '美元人民币',
  PMAU: '伦敦金',
  PMAG: '伦敦银',
  PMAP: '伦敦铂',
  PMPD: '伦敦钯',
  'GCM5-COMEX-FUT-USD': '美黄金',
  'SILK5-COMEX-FUT-USD': '美白银',
  'PLN5-NYMEX-FUT-USD': '美铂金',
  'PAM5-NYMEX-FUT-USD': '美钯金',
  黄金: '黄金',
  白银: '白银',
  铂金: '铂金',
  钯金: '钯金',
  铑金: '铑金',
  美铑金: '美铑金',
  港金: '港金',
  公斤条: '公斤条',
  美元指数: '美元指数',
  美元人民币: '美元人民币',
  伦敦金: '伦敦金',
  伦敦银: '伦敦银',
  伦敦铂: '伦敦铂',
  伦敦钯: '伦敦钯',
  美黄金: '美黄金',
  美白银: '美白银',
  美铂金: '美铂金',
  美钯金: '美钯金',
  黄金9999: '黄金9999',
  '黄金T+D': '黄金T+D',
  '白银T+D': '白银T+D',
  铂金9995: '铂金9995',
  Au9999: 'Au9999',
  'AuT+D': 'AuT+D',
  'AgT+D': 'AgT+D',
  Pt9995: 'Pt9995',
  金条价: '金条价',
  RKGRMB: '黄金',
  SKGRMB: '黄金',
  RKGSIV: '白银',
  AU9999: 'Au9999',
  AG9999: 'AgT+D',
};

function _syncAliases(sourceKey: string) {
  const s = _state[sourceKey];
  if (!s) return;
  const aliases = PRICE_ALIASES[sourceKey];
  if (!aliases) return;
  for (const alias of aliases) {
    if (_state[alias]) {
      _state[alias].prevPrice = _state[alias].price;
      _state[alias].price = s.price;
      _state[alias].bidPrice = s.bidPrice;
      _state[alias].basePrice = s.basePrice;
      _state[alias].changePct = s.changePct;
    }
  }
}

function _simulatePriceTick() {
  const elapsed = Date.now() - _lastRealTick;
  if (_isLive && elapsed > STALE_MS) {
    _isLive = false;
    _listeners.forEach((fn) => fn());
  }
}

function _startSimulation() {
  if (_simTimer) return;
  _simTimer = setInterval(_simulatePriceTick, 5000);
}

function _stopSimulation() {
  if (_simTimer) {
    clearInterval(_simTimer);
    _simTimer = null;
  }
}

function _stopWave(label?: string) {
  if (label) {
    if (_waveTimers[label]) {
      clearInterval(_waveTimers[label]);
      delete _waveTimers[label];
    }
  } else {
    for (const key of Object.keys(_waveTimers)) {
      clearInterval(_waveTimers[key]);
    }
    _waveTimers = {};
  }
}

function _extractField(item: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (key in item) return item[key];
  }
  return undefined;
}

function _markOffline() {
  _failCount++;
  if (_failCount >= MAX_FAIL_COUNT && _isLive) {
    _isLive = false;
    _listeners.forEach((fn) => fn());
  }
}

function _processWsData(data: unknown) {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (d.type === 'ping' || d.cmd === 'ping' || d.action === 'ping') {
      return;
    }
    if (d.type === 'pong' || d.cmd === 'pong') return;
  }

  let entries: [string, unknown][] = [];

  if (Array.isArray(data)) {
    entries = data.map((item) => {
      const s = (item && (item.s || item.symbol || item.code || item.sym || item.name)) as string;
      return [s, item];
    });
  } else if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    let inner: unknown = obj;
    if (Array.isArray(obj.data)) inner = obj.data;
    else if (Array.isArray(obj.result)) inner = obj.result;
    else if (Array.isArray(obj.items)) inner = obj.items;
    else if (Array.isArray(obj.list)) inner = obj.list;
    else if (Array.isArray(obj.quotes)) inner = obj.quotes;
    else if (Array.isArray(obj.prices)) inner = obj.prices;
    else if (Array.isArray(obj.tick)) inner = obj.tick;
    else if (Array.isArray(obj.ticks)) inner = obj.ticks;

    if (Array.isArray(inner)) {
      entries = inner.map((item) => {
        const s = (item && (item.s || item.symbol || item.code || item.sym || item.name)) as string;
        return [s, item];
      });
    } else {
      entries = Object.entries(inner as Record<string, unknown>);
    }
  }

  let updated = false;

  for (const [wsKey, rawItem] of entries) {
    if (!wsKey || wsKey === 'type' || wsKey === 'cmd' || wsKey === 'action' || wsKey === 'op' || wsKey === 'code') continue;
    const label = WS_TO_LABEL[wsKey];
    if (!label) {
      console.log('[LivePrices] 未知品种 key:', wsKey, '原始数据:', rawItem);
      continue;
    }

    let item: Record<string, unknown> = {};
    if (rawItem && typeof rawItem === 'object' && !Array.isArray(rawItem)) {
      item = rawItem as Record<string, unknown>;
    }

    const ask = parsePrice(_extractField(item, 'ask', 'a', 'sell', 'sale', 's', 'price', 'p', 'last', 'close', 'current', 'now', 'value'));
    const bid = parsePrice(_extractField(item, 'bid', 'b', 'buy', 'buyback', 'open', 'opening'));

    if (ask <= 0) continue;

    // 如果 bid 缺失或为 0，用 ask 的合理点差估算（黄金约 0.3-0.5，其他约 0.2%）
    let finalBid = bid;
    if (finalBid <= 0) {
      finalBid = ask * (label === '黄金' || label === 'Au9999' || label === '金条价' ? 0.9995 : 0.998);
    }

    let changePct = 0;
    const yesterday = parsePrice(_extractField(item, 'yesterday', 'preClose', 'prevClose', 'close', 'settlement', 'settle'));
    const open = parsePrice(_extractField(item, 'open', 'opening'));
    if (yesterday > 0) {
      changePct = ((ask - yesterday) / yesterday) * 100;
    } else if (open > 0) {
      changePct = ((ask - open) / open) * 100;
    } else if (bid > 0) {
      changePct = ((ask - bid) / bid) * 100;
    }

    const price = ask;

    let s = _state[label];
    if (!s) {
      _state[label] = {
        label,
        price,
        bidPrice: finalBid,
        basePrice: price,
        prevPrice: price,
        changePct,
      };
      _history[label] = Array(12).fill(price).map((p) => p + (Math.random() - 0.5) * p * 0.005);
      s = _state[label];
      updated = true;
    } else if (Math.abs(s.price - price) > 0.001 || Math.abs(s.bidPrice - bid) > 0.001 || Math.abs(s.changePct - changePct) > 0.001) {
      s.prevPrice = s.price;
      s.price = price;
      s.bidPrice = bid;
      s.basePrice = price;
      s.changePct = changePct;
      updated = true;
    }

    if (!_history[label]) _history[label] = [];
    _history[label].push(price);
    if (_history[label].length > 30) _history[label].shift();

    _syncAliases(label);

    console.log('[LivePrices] 更新', label, '销售价=', price, '回购价=', bid, '涨跌=', changePct.toFixed(2) + '%');
  }

  _lastRealTick = Date.now();
  _failCount = 0;
  if (!_isLive) {
    _isLive = true;
    _listeners.forEach((fn) => fn());
  }
  if (updated) {
    _listeners.forEach((fn) => fn());
  }
}

// ========== HTTP 轮询（通过 Edge Function 代理，前端不暴露外部 WebSocket 地址） ==========
function _buildHttpPollUrl(): string {
  const supabaseUrl = (import.meta.env as Record<string, string>)?.VITE_PUBLIC_SUPABASE_URL || '';
  if (supabaseUrl) {
    const base = supabaseUrl.replace(/\/$/, '');
    return `${base}/functions/v1/live-prices`;
  }
  return '';
}

async function _httpPollOnce() {
  if (!_httpPollUrl) return;
  try {
    const anonKey = (import.meta.env as Record<string, string>)?.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
    const res = await fetch(_httpPollUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
    });
    if (!res.ok) {
      console.log('[LivePrices] HTTP 轮询返回非 200:', res.status);
      return;
    }
    const data = await res.json();
    console.log('[LivePrices] HTTP 轮询返回:', data);

    if (!data || typeof data !== 'object') return;

    const arr: Record<string, unknown>[] = [];
    for (const [label, v] of Object.entries(data)) {
      if (label.startsWith('__')) continue;
      if (v === null || typeof v !== 'object') continue;
      const entry = v as Record<string, number>;
      let wsKey = label;
      for (const [k, mappedLabel] of Object.entries(WS_TO_LABEL)) {
        if (mappedLabel === label) { wsKey = k; break; }
      }
      arr.push({
        symbol: wsKey,
        name: label,
        price: entry.price ?? entry.ask ?? entry.sell ?? 0,
        bid: entry.bid ?? entry.buy ?? 0,
        changePct: entry.changePct ?? entry.change ?? 0,
      });
    }
    if (arr.length > 0) {
      console.log('[LivePrices] HTTP 轮询解析到', arr.length, '条价格');
      _processWsData(arr);
    }
  } catch (err) {
    console.log('[LivePrices] HTTP 轮询失败:', err);
    _markOffline();
  }
}

function _startHttpPoll() {
  if (_httpPollActive) return;
  _httpPollUrl = _buildHttpPollUrl();
  if (!_httpPollUrl) {
    console.log('[LivePrices] 无法构建 HTTP 轮询 URL，跳过兜底');
    return;
  }
  _httpPollActive = true;
  console.log('[LivePrices] 启动 HTTP 轮询:', _httpPollUrl);
  _httpPollOnce();
  _httpPollTimer = setInterval(_httpPollOnce, 5000);
}

function _stopHttpPoll() {
  if (_httpPollTimer) {
    clearInterval(_httpPollTimer);
    _httpPollTimer = null;
  }
  _httpPollActive = false;
}

function _startWs() {
  if (_ws || _wsReconnectTimer) return;
  if (typeof WebSocket === 'undefined') return;
  try {
    console.log('[LivePrices] WebSocket 连接:', WS_URL);
    _ws = new WebSocket(WS_URL);
    _ws.onopen = () => {
      console.log('[LivePrices] WebSocket 已连接');
      _wsReconnectCount = 0;
    };
    _ws.onmessage = (event) => {
      try {
        const rawData = event.data as string;
        if (rawData === 'ping' || rawData === 'Ping') {
          if (_ws && _ws.readyState === WebSocket.OPEN) {
            _ws.send('pong');
          }
          return;
        }
        const data = JSON.parse(rawData);
        if (data && typeof data === 'object') {
          if (data.type === 'ping' || data.cmd === 'ping' || data.action === 'ping') {
            if (_ws && _ws.readyState === WebSocket.OPEN) {
              _ws.send(JSON.stringify({ type: 'pong' }));
            }
            return;
          }
          if (data.type === 'pong' || data.cmd === 'pong') return;
        }
        _processWsData(data);
      } catch { /* ignore parse errors */ }
    };
    _ws.onerror = () => {
      console.log('[LivePrices] WebSocket 错误');
      _markOffline();
      _startHttpPoll(); // WebSocket 出错，启用 HTTP 兜底轮询
    };
    _ws.onclose = () => {
      console.log('[LivePrices] WebSocket 关闭');
      _ws = null;
      _startHttpPoll(); // WebSocket 断开，启用 HTTP 兜底轮询
      const delay = Math.min(5000 + _wsReconnectCount * 2000, 30000);
      _wsReconnectCount++;
      _wsReconnectTimer = setTimeout(() => {
        _wsReconnectTimer = null;
        _startWs();
      }, delay);
    };
  } catch {
    console.log('[LivePrices] WebSocket 初始化失败');
  }
}

function _stopWs() {
  if (_wsReconnectTimer) {
    clearTimeout(_wsReconnectTimer);
    _wsReconnectTimer = null;
  }
  if (_ws) {
    try { _ws.close(); } catch { /* ignore */ }
    _ws = null;
  }
}

async function _init() {
  if (_initialized) return;
  try {
    const { data, error } = await supabase
      .from('market_prices')
      .select('label, price, bid_price, change')
      .order('sort', { ascending: true });
    if (error) throw error;

    _state = {};
    _history = {};
    for (const row of data || []) {
      const label = String(row.label || '');
      if (!label) continue;
      const basePrice = parsePrice(row.price);
      const bidPrice = parsePrice(row.bid_price);
      const changePct = parsePrice(row.change);
      _state[label] = {
        label,
        price: basePrice,
        bidPrice: bidPrice > 0 ? bidPrice : basePrice,
        basePrice,
        prevPrice: basePrice,
        changePct: changePct !== 0 ? changePct : (Math.random() - 0.5) * 0.4,
      };
      _history[label] = Array(12).fill(basePrice).map((p) => p + (Math.random() - 0.5) * p * 0.005);
    }
  } catch {
    // ignore — fallback defaults below
  }

  for (const [label, price] of Object.entries(DEFAULT_PRICES)) {
    if (!_state[label]) {
      _state[label] = {
        label,
        price,
        bidPrice: price,
        basePrice: price,
        prevPrice: price,
        changePct: (Math.random() - 0.5) * 0.4,
      };
      _history[label] = Array(12).fill(price).map((p) => p + (Math.random() - 0.5) * p * 0.005);
    }
  }

  for (const [sourceKey, aliases] of Object.entries(PRICE_ALIASES)) {
    const s = _state[sourceKey];
    if (!s) continue;
    for (const alias of aliases) {
      if (_state[alias]) {
        _state[alias].basePrice = s.basePrice;
        _state[alias].price = s.price;
        _state[alias].bidPrice = s.bidPrice;
        _state[alias].prevPrice = s.price;
        _state[alias].changePct = s.changePct;
      }
    }
  }

  _initialized = true;
}

function _startTick() {
  if (_intervalId) {
    console.log('[PriceEngine] _startTick 被跳过，_intervalId 已存在');
    return;
  }
  console.log('[PriceEngine] _startTick 启动所有轮询');
  _intervalId = setInterval(() => {
    for (const key of Object.keys(_state)) {
      _syncAliases(key);
    }
  }, 3000);
  _startWs();       // 直接 WebSocket 连接
  _startHttpPoll(); // 常驻 HTTP 轮询，保持 market_prices 缓存新鲜（供下单秒回校验）
  _startSimulation();
}

function _stopTick() {
  console.log('[PriceEngine] _stopTick 停止所有轮询');
  if (_intervalId) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
  _stopWs();
  _stopHttpPoll();
  _stopSimulation();
  _stopWave();
}

export interface UseGoldPriceResult {
  prices: Record<string, number>;
  bidPrices: Record<string, number>;
  prevPrices: Record<string, number>;
  changePcts: Record<string, number>;
  isLive: boolean;
  getPrice: (label: string) => number;
  getBidPrice: (label: string) => number;
  getChange: (label: string) => number;
  getDirection: (label: string) => 'up' | 'down' | 'flat';
  formatPrice: (label: string, digits?: number) => string;
  formatBidPrice: (label: string, digits?: number) => string;
  getSparkline: (label: string, count?: number) => number[];
  simulateJump: (label: string, amount?: number) => void;
  simulateRandomJump: (label: string, range?: number) => void;
  simulateWave: (label: string, amplitudePct?: number, durationMs?: number) => void;
  stopWave: (label?: string) => void;
  setLivePrice: (label: string, price: number, bidPrice?: number) => void;
}

export function useGoldPrice(): UseGoldPriceResult {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!_initialized) {
      if (!_initPromise) {
        _initPromise = _init();
      }
      _initPromise
        .then(() => {
          forceUpdate((n) => n + 1);
        })
        .catch(() => {
          _initPromise = null;
        });
    }
    _startTick();

    const listener = () => {
      forceUpdate((n) => n + 1);
    };
    _listeners.add(listener);
    return () => {
      _listeners.delete(listener);
      if (_listeners.size === 0) {
        _stopTick();
      }
    };
  }, []);

  const getPrice = useCallback((label: string): number => {
    return _state[label]?.price || 0;
  }, []);

  const getBidPrice = useCallback((label: string): number => {
    const s = _state[label];
    if (!s) return 0;
    if (s.bidPrice > 0) return s.bidPrice;
    if (s.price > 0) return s.price;
    return 0;
  }, []);

  const getChange = useCallback((label: string): number => {
    return _state[label]?.changePct || 0;
  }, []);

  const getDirection = useCallback((label: string): 'up' | 'down' | 'flat' => {
    const s = _state[label];
    if (!s) return 'flat';
    if (s.price > s.prevPrice) return 'up';
    if (s.price < s.prevPrice) return 'down';
    return 'flat';
  }, []);

  const formatPrice = useCallback((label: string, digits = 2): string => {
    const p = _state[label]?.price || 0;
    return p.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }, []);

  const formatBidPrice = useCallback((label: string, digits = 2): string => {
    const p = _state[label]?.bidPrice || 0;
    return p.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }, []);

  const simulateJump = useCallback((label: string, amount = 40): void => {
    const s = _state[label];
    if (!s || s.price <= 0) return;
    const next = s.price + amount;
    s.prevPrice = s.price;
    s.price = next;
    s.changePct = ((next - s.basePrice) / s.basePrice) * 100;
    if (!_history[label]) _history[label] = [];
    _history[label].push(next);
    if (_history[label].length > 30) _history[label].shift();
    _syncAliases(label);
    _listeners.forEach((fn) => fn());
  }, []);

  const simulateRandomJump = useCallback((label: string, range = 40): void => {
    const amount = (Math.random() > 0.5 ? 1 : -1) * Math.random() * range;
    simulateJump(label, amount);
  }, [simulateJump]);

  const simulateWave = useCallback((label: string, amplitudePct = 0.30, durationMs = 120000): void => {
    const s = _state[label];
    if (!s || s.price <= 0) return;

    _stopWave(label);
    const anchor = s.price;
    const low = anchor * (1 - amplitudePct);
    const startTime = Date.now();

    _waveTimers[label] = setInterval(() => {
      const cur = _state[label];
      if (!cur) {
        _stopWave();
        return;
      }

      const cycleElapsed = (Date.now() - startTime) % durationMs;
      const progress = cycleElapsed / durationMs;

      let target: number;
      if (progress < 1 / 3) {
        const t = progress / (1 / 3);
        target = anchor + (low - anchor) * t;
      } else if (progress < 2 / 3) {
        const t = (progress - 1 / 3) / (1 / 3);
        target = low + (anchor - low) * t;
      } else {
        const t = (progress - 2 / 3) / (1 / 3);
        target = anchor + (low - anchor) * t;
      }

      const next = Math.round(target * 1000) / 1000;
      if (next === cur.price) return;

      cur.prevPrice = cur.price;
      cur.price = next;
      cur.changePct = ((next - cur.basePrice) / cur.basePrice) * 100;

      if (!_history[label]) _history[label] = [];
      _history[label].push(next);
      if (_history[label].length > 30) _history[label].shift();

      _syncAliases(label);
      _listeners.forEach((fn) => fn());
    }, 200);
  }, []);

  const stopWave = useCallback((): void => {
    _stopWave();
  }, []);

  const setLivePrice = useCallback((label: string, price: number, bidPrice?: number): void => {
    const s = _state[label];
    if (!s || price <= 0) return;
    s.prevPrice = s.price;
    s.price = price;
    if (bidPrice !== undefined && bidPrice > 0) s.bidPrice = bidPrice;
    if (s.basePrice > 0) s.changePct = ((price - s.basePrice) / s.basePrice) * 100;
    if (!_history[label]) _history[label] = [];
    _history[label].push(price);
    if (_history[label].length > 30) _history[label].shift();
    _syncAliases(label);
    _listeners.forEach((fn) => fn());
  }, []);

  const getSparkline = useCallback((label: string, count = 12): number[] => {
    const arr = _history[label] || [];
    if (arr.length === 0) return Array(count).fill(getPrice(label) || 0);
    if (arr.length >= count) return arr.slice(-count);
    const last = arr[arr.length - 1];
    const padding = Array(count - arr.length).fill(last);
    return [...padding, ...arr];
  }, [getPrice]);

  const bidPrices: Record<string, number> = {};
  const prices: Record<string, number> = {};
  const prevPrices: Record<string, number> = {};
  const changePcts: Record<string, number> = {};
  for (const key of Object.keys(_state)) {
    prices[key] = _state[key].price;
    bidPrices[key] = _state[key].bidPrice;
    prevPrices[key] = _state[key].prevPrice;
    changePcts[key] = _state[key].changePct;
  }

  return { prices, bidPrices, prevPrices, changePcts, isLive: _isLive, getPrice, getBidPrice, getChange, getDirection, formatPrice, formatBidPrice, getSparkline, simulateJump, simulateRandomJump, simulateWave, stopWave, setLivePrice };
}

export function calcProfit(direction: string, weight: number, openPrice: number, currentPrice: number): number {
  if (direction === 'IN') {
    return (currentPrice - openPrice) * weight;
  }
  if (direction === 'OUT') {
    return (openPrice - currentPrice) * weight;
  }
  return 0;
}

export function calcPositionLive(
  direction: string,
  weight: number,
  openPrice: number,
  livePrice: number,
  currentMargin: number,
  originalMargin: number,
) {
  const liveProfit = calcProfit(direction, weight, openPrice, livePrice);
  // 保证金率 = 当前净值 / 初始保证金（锚定初始保证金，追加不改变分母）
  const denominator = originalMargin > 0 ? originalMargin : currentMargin > 0 ? currentMargin : 1;
  const marginRate = ((currentMargin + liveProfit) / denominator) * 100;
  return { liveProfit, marginRate };
}

export function calcAccountLive(
  baseAvailable: number,
  baseUsed: number,
) {
  return { liveAvailable: baseAvailable, liveUsed: baseUsed };
}